// Página de Análisis de Datos (ruta "/data").
// Muestra dos secciones con posts traídos de Ghost CMS:
//   - "Mis Proyectos": posts con etiqueta 'data-proyect'
//   - "Noticias y Artículos": posts con etiqueta 'datos'
// Ambas secciones tienen paginación con botón "Cargar más".
// Mientras cargan los posts muestra SkeletonCards.
// Si la API falla muestra un mensaje de error en lugar de quedar en blanco.

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaLinkedin, FaArrowLeft } from 'react-icons/fa';
import Footer from '../../../components/footer/Footer';
import InfoCard from '../../../components/shared/InfoCard';
import SkeletonCard from '../../../components/shared/SkeletonCard';
import ProductRow from '../../../components/store/ProductRow';
import { getGhostPosts } from '../../../services/ghost';
import styles from './data-analisys.module.css';

// Cantidad de posts que se muestran por página en cada sección
const POSTS_PER_PAGE = 6;

// Muestra entre 1 y POSTS_PER_PAGE skeletons según cuántos posts se esperan
const SkeletonGrid = ({ count = POSTS_PER_PAGE }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </>
);

const DataAnalysis = () => {
  const [projectPosts, setProjectPosts] = useState([]);
  const [newsPosts, setNewsPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const [projectsPage, setProjectsPage] = useState(1);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const [isLoadingMoreProjects, setIsLoadingMoreProjects] = useState(false);

  const [newsPage, setNewsPage] = useState(1);
  const [hasMoreNews, setHasMoreNews] = useState(true);
  const [isLoadingMoreNews, setIsLoadingMoreNews] = useState(false);

  // Carga inicial: trae proyectos y noticias al mismo tiempo en paralelo
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const [projectsData, newsData] = await Promise.all([
          getGhostPosts({ tagSlug: 'data-proyect', limit: POSTS_PER_PAGE, page: 1 }),
          getGhostPosts({ tagSlug: 'datos', limit: POSTS_PER_PAGE, page: 1 }),
        ]);
        setProjectPosts(projectsData.posts || []);
        setNewsPosts(newsData.posts || []);
        setHasMoreProjects((projectsData.posts || []).length === POSTS_PER_PAGE);
        setHasMoreNews((newsData.posts || []).length === POSTS_PER_PAGE);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // Agrega la siguiente página de proyectos al final de la lista existente
  const handleLoadMoreProjects = async () => {
    if (isLoadingMoreProjects) return;
    setIsLoadingMoreProjects(true);
    const nextPage = projectsPage + 1;
    try {
      const data = await getGhostPosts({ tagSlug: 'data-proyect', limit: POSTS_PER_PAGE, page: nextPage });
      setProjectPosts(prev => [...prev, ...(data.posts || [])]);
      setProjectsPage(nextPage);
      if ((data.posts || []).length < POSTS_PER_PAGE) setHasMoreProjects(false);
    } catch {
      // Error silencioso: el botón queda disponible para reintentar
    } finally {
      setIsLoadingMoreProjects(false);
    }
  };

  // Agrega la siguiente página de noticias al final de la lista existente
  const handleLoadMoreNews = async () => {
    if (isLoadingMoreNews) return;
    setIsLoadingMoreNews(true);
    const nextPage = newsPage + 1;
    try {
      const data = await getGhostPosts({ tagSlug: 'datos', limit: POSTS_PER_PAGE, page: nextPage });
      setNewsPosts(prev => [...prev, ...(data.posts || [])]);
      setNewsPage(nextPage);
      if ((data.posts || []).length < POSTS_PER_PAGE) setHasMoreNews(false);
    } catch {
      // Error silencioso: el botón queda disponible para reintentar
    } finally {
      setIsLoadingMoreNews(false);
    }
  };

  return (
    <div className={styles['page-wrapper']}>
      <Helmet>
        <title>Análisis de Datos — Matias Cagliero</title>
        <meta name="description" content="Proyectos de Business Intelligence, SQL y visualización de datos. Artículos sobre análisis aplicado a negocios." />
        <link rel="canonical" href="https://cagliero.ar/data" />
        <meta property="og:title" content="Análisis de Datos — Matias Cagliero" />
        <meta property="og:description" content="Proyectos de Business Intelligence, SQL y visualización de datos. Artículos sobre análisis aplicado a negocios." />
        <meta property="og:url" content="https://cagliero.ar/data" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Barra superior con breadcrumb y link a LinkedIn */}
      <div className={styles['top-bar']}>
        <nav className={styles.breadcrumb}>
          <Link to="/" className={styles['back-button-breadcrumb']}>
            <FaArrowLeft className={styles['back-icon']} />
            <span>Inicio</span>
          </Link>
          <span className={styles['breadcrumb-separator']}>/</span>
          <span className={styles['breadcrumb-current']}>Análisis de Datos</span>
        </nav>
        <a
          href="https://www.linkedin.com/in/cagliero/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles['social-icon']}
          aria-label="LinkedIn de Matias Cagliero"
        >
          <FaLinkedin />
        </a>
      </div>

      <header className={styles['hero-section-v3']}>
        <div className={styles['hero-content']}>
          <h1 className={styles['hero-title']}>Análisis de Datos</h1>
          <p className={styles['hero-subtitle']}>
            Convirtiendo datos en decisiones. Proyectos, novedades y cómo aplicar el análisis a un negocio.
          </p>
          <div style={{ marginTop: '2rem' }}>
            <Link to="/form?tipo=data" className={styles['hero-cta-btn']}>
              Suscribite a mis publicaciones
            </Link>
          </div>
        </div>
      </header>

      <main className={styles['data-analysis-content-area']}>

        {/* Mensaje de error si la API de Ghost no responde */}
        {error && (
          <p className={styles['loading-message']}>
            No se pudo cargar el contenido. Verificá tu conexión e intentá recargar la página.
          </p>
        )}

        {/* Sección de proyectos personales */}
        <section className={styles['section-v3']}>
          <h2 className={styles['section-title-v3']}>Mis Proyectos</h2>
          <div className={styles['posts-grid-container']}>
            {isLoading
              ? <SkeletonGrid />
              : projectPosts.map(post => <InfoCard key={post.id} item={post} variant="compact" />)
            }
          </div>
          <div className={styles['load-more-container']}>
            {!isLoading && hasMoreProjects && (
              <button
                onClick={handleLoadMoreProjects}
                className={styles['load-more-btn']}
                disabled={isLoadingMoreProjects}
              >
                {isLoadingMoreProjects ? 'Cargando...' : 'Cargar más proyectos'}
              </button>
            )}
          </div>
        </section>

        {/* Fila de productos del ecommerce (si está habilitada en el admin) */}
        <ProductRow category="data" />

        {/* Sección de artículos y noticias del sector */}
        <section className={styles['section-v3']}>
          <h2 className={styles['section-title-v3']}>Noticias y Artículos</h2>
          <div className={styles['posts-grid-container']}>
            {isLoading
              ? <SkeletonGrid />
              : newsPosts.map(post => <InfoCard key={post.id} item={post} variant="list" />)
            }
          </div>
          <div className={styles['load-more-container']}>
            {!isLoading && hasMoreNews && (
              <button
                onClick={handleLoadMoreNews}
                className={styles['load-more-btn']}
                disabled={isLoadingMoreNews}
              >
                {isLoadingMoreNews ? 'Cargando...' : 'Cargar más noticias'}
              </button>
            )}
          </div>
        </section>

      </main>

      <Footer variant="data" />
    </div>
  );
};

export default DataAnalysis;
